<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <v-infinite-scroll @load="load">
                    <v-data-table-server
                        v-model:expanded="expanded"
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="rechnungenList"
                        item-value="pdfDokumentGuid"
                        show-expand
                        :loading="loading"
                        hide-default-footer
                        class="elevation-1"
                        :items-length="totalNumberOfRecords || 0"
                        :must-sort="true"
                        :custom-sort="customSort"
                        hover
                        @click:row="expandRow"
                        @update:sort-by="onSortByChanged"
                    >
                        <template #top>
                            <div>
                                <TableHeader :filters="filters" @chip-closed="removeFilter">
                                    <template #default>
                                        <div class="table-header-item-count">
                                            {{ totalNumberOfRecords + ' ' + $t('archiv.table.header.items') }}
                                        </div>
                                        <v-text-field
                                            v-if="showSearchBar"
                                            v-model="filterData.suchText"
                                            class="ml-5"
                                            prepend-inner-icon="mdi-magnify"
                                            append-inner-icon="mdi-close"
                                            :label="$t('rechnungen.suchfeld')"
                                            hide-details
                                            autofocus
                                            @update:model-value="searchFieldInputChanged"
                                            @click:append-inner="searchBarClosed"
                                        ></v-text-field>
                                        <v-spacer />
                                        <v-tooltip top color="secondary">
                                            <template #activator="{ props }">
                                                <v-btn v-bind="props" icon color="primary">
                                                    <v-icon v-if="!showSearchBar" @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                                </v-btn>
                                            </template>
                                            <div>
                                                <span>
                                                    {{ $t('tableheader.tooltip.search') }}
                                                </span>
                                            </div>
                                        </v-tooltip>
                                        <v-tooltip top color="secondary">
                                            <template #activator="{ props }">
                                                <v-btn v-bind="props" icon color="primary" @click="showFilterDrawer = !showFilterDrawer">
                                                    <v-icon>mdi-filter-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <div>
                                                <span>
                                                    {{ $t('tableheader.tooltip.filter') }}
                                                </span>
                                            </div>
                                        </v-tooltip>
                                    </template>
                                </TableHeader>
                            </div>
                        </template>
                        <template #item.lieferantName="{ item }">
                            <TextHighlights :text="item.lieferantName" :search-text="filterData.suchText" />
                        </template>
                        <template #item.rechnungNummer="{ item }">
                            <TextHighlights :text="item.rechnungNummer" :search-text="filterData.suchText" />
                        </template>
                        <template #item.betrag="{ item }">
                            <span v-if="item.lieferantDokumentTyp === 'Rechnung'">
                                <span class="text-red">-{{ formatNumber(item.betrag) }}</span>
                            </span>
                            <span v-else>
                                {{ formatNumber(item.betrag) }}
                            </span>
                        </template>
                        <template #item.link="{ item }">
                            <v-btn
                                icon
                                class="elevation-0"
                                :href="item.link"
                                @click.stop
                                @click="
                                    downloadBeleg({
                                        id: item.pdfDokumentGuid,
                                        defaultFileName: `${item.pdfDokumentGuid}.pdf`
                                    })
                                "
                            >
                                <v-icon color="primary" icon="mdi-download-outline" />
                            </v-btn>
                        </template>
                        <template #item.xml="{ item }">
                            <v-btn
                                icon
                                class="elevation-0"
                                :href="item.link"
                                @click.stop
                                @click="
                                    downloadBeleg({
                                        id: item.xmlDokumentGuid,
                                        defaultFileName: `${item.xmlDokumentGuid}.xml`
                                    })
                                "
                            >
                                <v-icon color="primary" icon="mdi-download-outline" />
                            </v-btn>
                        </template>

                        <!-- Override default expand button -->
                        <template #item.data-table-expand="{ item }">
                            <v-btn icon class="elevation-0">
                                <v-icon>{{ isRowExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </v-btn>
                        </template>

                        <!-- expanded row -->
                        <template #expanded-row="{}">
                            <td class="d-none d-sm-table-cell"></td>
                            <td :colspan="5" class="pr-5">
                                <v-row>
                                    <v-col>
                                        <v-data-table
                                            :headers="subHeaders"
                                            :items="rechnungDetails"
                                            disable-pagination
                                            density="compact"
                                            item-value="produktNummer"
                                            class="bg-surface-light elevation-2 rounded-b-lg pa-3 ma-3"
                                            hide-default-footer
                                            hover
                                            :items-per-page="-1"
                                        >
                                            <template #item.produkteNummer="{ item }">
                                                <TextHighlights :text="item.produkteNummer" :search-text="filterData.suchText" />
                                            </template>
                                            <template #item.produktBezeichnung="{ item }">
                                                <a v-if="item.produktNummerVetpoint" style="text-decoration: underline">
                                                    <TextHighlights
                                                        :text="item.produktBezeichnung"
                                                        :search-text="filterData.suchText"
                                                        @click="openProduktNummerVetpointTab(item.produktNummerVetpoint)"
                                                    />
                                                </a>
                                                <TextHighlights
                                                    v-else
                                                    :text="item.produktBezeichnung"
                                                    :search-text="filterData.suchText"
                                                    @click="openProduktNummerVetpointTab(item.produktNummerVetpoint)"
                                                />
                                            </template>
                                            <template #item.anzahl="{ item }">
                                                <span v-text="item.anzahl.toFixed(3)" />
                                            </template>
                                            <template #item.einheit="{ item }">
                                                <span v-if="language === 'de'">{{ item.einheitTextDe }} </span>
                                                <span v-if="language === 'fr'">{{ item.einheitTextFr }} </span>
                                            </template>
                                            <template #item.einzelPreis="{ item }">
                                                <span v-text="formatNumber(item.einzelPreis)" />
                                            </template>
                                            <template #item.betragInklusivMwst="{ item }">
                                                <span v-text="formatNumber(item.betragInklusivMwst)" />
                                            </template>
                                        </v-data-table>
                                    </v-col>
                                </v-row>
                            </td>
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-col>
        </v-row>
        <FilterDrawer
            v-model:visible="showFilterDrawer"
            @cancel="cancelFilter()"
            @confirm="confirmFilter()"
            @clear="clearAllFilters(true)"
            @reset="clearAllFilters(false)"
        >
            <SingleFilter :title="$t('rechnungen.filter.header.tvskundevonzugriffe')">
                <template #default>
                    <v-row>
                        <v-col sm="12">
                            <v-select
                                v-model="filterData.tvsKundeTeilnehmerIdsForFilter"
                                variant="filled"
                                density="compact"
                                hide-details
                                :items="tvsKundenVonLieferant"
                            ></v-select>
                        </v-col>
                    </v-row>
                </template>
            </SingleFilter>
            <SingleFilter title="">
                <template #default>
                    <div>
                        <div>
                            <v-radio-group v-model="radios" class="mt-0" inline>
                                <h5 class="pb-0">{{ $t('archiv.filter.title.datum') }}</h5>
                                <v-col cols="12">
                                    <v-radio value="specificTime" :label="$t('rechnungen.filter.label.specifictime')"></v-radio>
                                </v-col>
                                <v-col cols="12" class="pt-0">
                                    <v-row>
                                        <v-col cols="6">
                                            <NowhowDatepicker
                                                v-model="filterData.rechnungDatumVon"
                                                :disabled="radios !== 'specificTime'"
                                                label="archiv.filter.datum.von"
                                                @change="onRechnungsDatumVonChange"
                                            >
                                            </NowhowDatepicker>
                                        </v-col>
                                        <v-col cols="6">
                                            <NowhowDatepicker
                                                v-model="filterData.rechnungDatumBis"
                                                :disabled="radios !== 'specificTime'"
                                                label="archiv.filter.datum.bis"
                                                @change="onRechnungsDatumBisChange"
                                            >
                                            </NowhowDatepicker>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col cols="12" class="pt-0">
                                    <v-radio value="selectTime" :label="$t('rechnungen.filter.label.selecttime')"></v-radio>
                                </v-col>
                                <v-col cols="12" class="pt-0 pb-8">
                                    <v-select
                                        v-model="filterData.zeitspanne"
                                        :disabled="radios !== 'selectTime'"
                                        variant="filled"
                                        density="compact"
                                        hide-details
                                        :items="ddTimeItems"
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" class="pt-0 pb-0">
                                    <v-radio value="allTime" :label="$t('kontobrowser.filter.alltime')"></v-radio>
                                </v-col>
                            </v-radio-group>
                        </div>
                        <div>
                            <h5 class="pb-2">
                                {{ $t('kontobrowser.filter.title.betrag') }}
                            </h5>
                            <v-row class="pb-6 filterLine">
                                <v-col cols="6">
                                    <v-text-field
                                        v-model="filterData.betragVon"
                                        hide-details
                                        variant="filled"
                                        density="compact"
                                        :label="$t('rechnungen.filter.label.betragvon')"
                                        @keypress="checkInputCharacter($event)"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model="filterData.betragBis"
                                        hide-details
                                        variant="filled"
                                        density="compact"
                                        :label="$t('rechnungen.filter.label.betragbis')"
                                        @keypress="checkInputCharacter($event)"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </div>
                    </div>
                </template>
            </SingleFilter>
        </FilterDrawer>
    </v-container>
</template>

<script lang="ts">
import { Component, Watch, mixins } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import { DateMixin } from '@/mixins/DateMixin';
import { ZeitspanneFilterApiDto, TvsLieferantRechnungApiDto, TvsLieferantRechnungDetailApiDto } from '@/api-schema';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
const generalModul = namespace(NAME_GENERAL);
import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';

import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
const headerNotificationModul = namespace(NAME_HEADERNOTIFICATION);
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';

import { NAME as NAME_SECURITY } from '@/store/modules/security';
const securityModul = namespace(NAME_SECURITY);
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';

import { NAME as NAME_LIEFERANT } from '../store';
const lieferantModul = namespace(NAME_LIEFERANT);
import { GET_ALL_RECHNUNGEN, GET_RECHNUNG_DETAILS, DOWNLOAD_BELEG, GET_ALL_TVS_KUNDEN_VON_LIEFERANT } from '../store/actions';
import { APPLYFILTER, CLEARFILTER } from '../store/mutations';
import { RECHNUNGEN, RECHNUNGDETAILS, LOADING, TOTAL_NUMBER_OF_RECORDS, GET_TVS_KUNDEN_VON_LIEFERANT } from '../store/getters';

import { NAME as NAME_RESOURCE } from '@/store/modules/resource';
const resourceModul = namespace(NAME_RESOURCE);
import { GET_LANGUAGE } from '@/store/modules/resource/getters';

import { RechnungFilterDto } from '@/types/RechnungFilterDto';

import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TableHeader from '@/components/TableHeader.vue';
import NowhowDatepicker from '@/components/NowhowDatepicker.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import debounce from 'lodash/debounce';
import { TableHeaderFilterItem } from '@/types/TableHeaderFilterItem';
import { GET_ALL_TVS_KUNDEN_VON_ZUGRIFFE } from '@/areas/praxisgruppe/store/actions';
@Component({
    components: {
        SingleFilter,
        FilterDrawer,
        TableHeader,
        NowhowDatepicker,
        TextHighlights
    }
})
export default class LieferantRechnungen extends mixins(DateMixin) {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @headerNotificationModul.Action(NEW_ALERT) alertMessage!: Function;

    @lieferantModul.Action(GET_ALL_RECHNUNGEN) getAll!: Function;
    @lieferantModul.Action(GET_RECHNUNG_DETAILS) getDetails!: Function;
    @lieferantModul.Action(DOWNLOAD_BELEG) downloadBeleg!: Function;
    @lieferantModul.Action(GET_ALL_TVS_KUNDEN_VON_LIEFERANT) getAllTvsKundenVonLieferant!: Function;
    @lieferantModul.Getter(RECHNUNGEN)
    rechnungenList!: Array<TvsLieferantRechnungApiDto>;
    @lieferantModul.Getter(RECHNUNGDETAILS)
    rechnungDetails!: Array<TvsLieferantRechnungDetailApiDto>;
    @lieferantModul.Getter(LOADING) loading!: boolean;
    @lieferantModul.Getter(GET_TVS_KUNDEN_VON_LIEFERANT)
    tvsKundenVonLieferant!: { title: string; value: string }[];
    @lieferantModul.Getter(TOTAL_NUMBER_OF_RECORDS) totalNumberOfRecords!: number;
    @lieferantModul.Mutation(CLEARFILTER) clearFilters!: Function;
    @lieferantModul.Mutation(APPLYFILTER) applyFilters!: Function;
    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;
    @resourceModul.Getter(GET_LANGUAGE) language!: string;

    file = null;
    showSearchBar = false;
    searchField = '';
    filters: Array<TableHeaderFilterItem> = [];
    expanded = [];
    title = '';
    filterData: RechnungFilterDto = new RechnungFilterDto();
    filterDataCopy: RechnungFilterDto = new RechnungFilterDto();
    showFilterDrawer = false;
    radios = 'selectTime';
    radiosCopy = '';
    sortBy: [{ key: 'rechnungDatum'; order: 'desc' }];
    infiniteLoad = null;

    get ddTimeItems(): Array<object> {
        return [
            {
                title: this.$t('kontobrowser.filter.timeitems.letztezeit'),
                value: 'Last60Days' as ZeitspanneFilterApiDto
            },
            {
                title: this.$t('kontobrowser.filter.timeitems.letzte180tage'),
                value: 'Last180Days' as ZeitspanneFilterApiDto
            },
            {
                title: this.$t('kontobrowser.filter.timeitems.aktuelljahr'),
                value: 'CurrentYear' as ZeitspanneFilterApiDto
            },
            {
                title: this.$t('kontobrowser.filter.timeitems.vorjahr'),
                value: 'LastYear' as ZeitspanneFilterApiDto
            }
        ];
    }
    get headers(): Array<any> {
        return [
            { title: '', key: 'data-table-expand', sortable: false }, // Expand button
            {
                title: this.$t('rechnungen.table.header.datum'),
                key: 'rechnungDatum'
            },
            {
                title: this.$t('rechnungen.table.header.rechnungnummer'),
                key: 'rechnungNummer'
            },
            {
                title: this.$t('rechnungen.table.header.praxisbezeichnung'),
                key: 'praxisbezeichnung',
                sortable: false
            },
            {
                title: this.$t('rechnungen.table.header.betrag'),
                key: 'betrag',
                align: 'end'
            },
            {
                title: this.$t('rechnungen.table.header.beleg'),
                key: 'link',
                align: 'center',
                width: '150',
                sortable: false
            },
            {
                title: this.$t('rechnungen.table.header.beleg.xml'),
                key: 'xml',
                align: 'center',
                width: '150',
                sortable: false
            }
        ];
    }
    get subHeaders(): Array<any> {
        return [
            {
                title: this.$t('rechnungen.table.subheader.produktnummer'),
                key: 'produkteNummer'
            },
            {
                title: this.$t('rechnungen.table.subheader.bezeichnung'),
                key: 'produktBezeichnung'
            },
            {
                title: this.$t('rechnungen.table.subheader.anzahl'),
                key: 'anzahl',
                align: 'end'
            },
            {
                title: this.$t('rechnungen.table.subheader.einheit'),
                key: 'einheit'
            },
            {
                title: this.$t('rechnungen.table.subheader.einzelpreis'),
                key: 'einzelPreis',
                align: 'end'
            },
            {
                title: this.$t('rechnungen.table.subheader.betrag'),
                key: 'betragInklusivMwst',
                align: 'end'
            }
        ];
    }
    created() {
        this.setSubTitle(this.$t('rechnung.subtitle'));
        this.setFooterTitle('S500');
    }

    mounted() {
        if (!this.hasAccess('Navigation.LieferantRechnungen')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
            this.filterData.zeitspanne = 'Last180Days';
            this.filters.push({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: this.$t('rechnungen.filter.chip.value.last180').toString()
            });
            this.getAllTvsKundenVonLieferant();
            this.applyFilters(this.filterData);
            this.getAll(0);
        }
        this.searchFieldInputChanged = debounce(() => {
            this.applyFilters(this.filterData);
            this.getAll(0);
        }, 400);
    }

    /* Funktion deklarieren, aber erst in mount mit debounce initialisieren */
    searchFieldInputChanged!: () => void;

    private openProduktNummerVetpointTab(produktNummerVetpoint: string) {
        if (produktNummerVetpoint != null) {
            window.open(`https://www.vetpoint.ch/ecommerce/control/product?product_id=${produktNummerVetpoint}`);
        }
    }

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.suchText = undefined;
        this.applyFilters(this.filterData);
        this.getAll(0);
    }

    private expandRow(event: PointerEvent, row: any) {
        if (this.expanded.includes(row.item.pdfDokumentGuid)) {
            this.expanded = [];
        } else {
            this.expanded = [];
            this.expanded.push(row.item.pdfDokumentGuid);
            this.getDetails(row.item.xmlDokumentGuid);
        }
    }

    private ShowActiveFilters() {
        this.filters = [];
        if (this.radios === 'allTime') {
            this.filterData.rechnungDatumVon = undefined;
            this.filterData.rechnungDatumBis = undefined;
            this.filterData.zeitspanne = undefined;
        }
        if (this.radios === 'specificTime') {
            let filterValue = '';
            if (this.filterData.rechnungDatumVon && this.filterData.rechnungDatumBis) {
                filterValue = this.filterData.rechnungDatumVon + ' - ' + this.filterData.rechnungDatumBis;
            } else if (this.filterData.rechnungDatumVon) {
                filterValue = '<= ' + this.filterData.rechnungDatumVon;
            } else if (this.filterData.rechnungDatumBis) {
                filterValue = '>= ' + this.filterData.rechnungDatumBis;
            }

            this.filters = this.filters.concat({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: filterValue
            });
        }
        if (this.radios === 'selectTime') {
            this.filterData.rechnungDatumVon = undefined;
            this.filterData.rechnungDatumBis = undefined;
            if (this.filterData.zeitspanne === 'Last60Days') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.recent').toString()
                });
            } else if (this.filterData.zeitspanne === 'Last180Days') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.last180').toString()
                });
            } else if (this.filterData.zeitspanne === 'CurrentYear') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.currentyear').toString()
                });
            } else if (this.filterData.zeitspanne === 'LastYear') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.lastyear').toString()
                });
            }
        }
        if (this.filterData.betragVon !== undefined && this.filterData.betragBis) {
            this.filters = this.filters.concat({
                key: 'rechnungen.filter.chip.title.betrag',
                nameResourceKey: 'rechnungen.filter.chip.title.betrag',
                value: this.filterData.betragVon + ' - ' + this.filterData.betragBis
            });
        }
        if (this.filterData.tvsKundeTeilnehmerIdsForFilter) {
            const kunde = this.tvsKundenVonLieferant.find((kunde) => kunde.value === this.filterData.tvsKundeTeilnehmerIdsForFilter);
            if (kunde?.title) {
                this.filters = this.filters.concat({
                    key: 'rechnungen.filter.header.tvskundevonzugriffe',
                    nameResourceKey: 'rechnungen.filter.header.tvskundevonzugriffe',
                    value: kunde.title
                });
            }
        }
    }

    private customSort(items: any[]) {
        return items;
    }

    private removeFilter(value: string) {
        console.log('removeFilter: ' + value + 'removed');
        this.filters = this.filters.filter(function (obj) {
            return obj.key !== value;
        });
        if (value === 'archiv.filter.chip.title.datum') {
            this.filterData.rechnungDatumBis = undefined;
            this.filterData.rechnungDatumVon = undefined;
            this.filterData.zeitspanne = undefined;
            this.radios = 'allTime';
        } else if (value === 'rechnungen.filter.chip.title.betrag') {
            this.filterData.betragVon = undefined;
            this.filterData.betragBis = undefined;
        } else if (value === 'rechnungen.filter.header.tvskundevonzugriffe') {
            this.filterData.tvsKundeTeilnehmerIdsForFilter = undefined;
        }
        this.applyFilters(this.filterData);
        this.getAll(0);
    }

    private clearAllFilters(closeFilter: boolean) {
        this.radios = 'selectTime';
        this.filterData = new RechnungFilterDto();
        this.filterData.rechnungDatumBis = undefined;
        this.filterData.rechnungDatumVon = undefined;
        this.filterData.zeitspanne = 'Last180Days';
        this.filterData.tvsKundeTeilnehmerIdsForFilter = undefined;
        if (closeFilter) {
            this.clearFilters();
            this.filters = [];
            this.applyFilters(this.filterData);
            this.showFilterDrawer = false;
        }
    }

    async load({ done }) {
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            // initialer datenload abwarten, sonst wird fälschlicherweise 'empty' emitted
            done('ok');
            return;
        }
        await this.getAll(this.rechnungenList.length);
        if (this.rechnungenList.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }

    private confirmFilter() {
        this.ShowActiveFilters();
        this.applyFilters(this.filterData);
        this.getAll(0);
        this.showFilterDrawer = false;
    }

    private cancelFilter() {
        this.filterData = Object.assign({}, this.filterDataCopy);
        this.radios = this.radiosCopy;
        this.showFilterDrawer = false;
    }

    @Watch('showFilterDrawer')
    onFilterDrawerOpened() {
        if (this.showFilterDrawer) {
            this.getAllTvsKundenVonLieferant();
            this.filterDataCopy = Object.assign({}, this.filterData);
            this.radiosCopy = this.radios;
        }
    }

    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0]; // { key: 'lieferantName', order: 'desc' }

            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }
        this.applyFilters(this.filterData);
        this.getAll(0);
    }

    @Watch('language')
    onLanugageChanged() {
        this.ShowActiveFilters();
    }

    onRechnungsDatumVonChange(newValue: string) {
        if (newValue) {
            this.filterData.rechnungDatumVon = newValue;
        }
    }
    onRechnungsDatumBisChange(newValue: string) {
        if (newValue) {
            this.filterData.rechnungDatumBis = newValue;
        }
    }
    isRowExpanded(item) {
        return this.expanded.includes(item.pdfDokumentGuid);
    }
    checkInputCharacter(evt: any) {
        evt = evt ? evt : window.event;
        const charCode = evt.which ? evt.which : evt.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
            evt.preventDefault();
        } else {
            return true;
        }
    }
    formatNumber(value: number): string {
        return new Intl.NumberFormat('de-CH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
